import { SignupProps } from './Signup.props';

import { useMutation } from '@tanstack/react-query';
import { googleLogin, register } from 'api/services/auth';
import { routesSettings } from 'constants/routes';
import { useNavigate } from 'react-router-dom';
import { $session } from 'state/stores';
import { $user } from 'state/stores/user';
import { GoogleSignupPayload, RegisterPayload } from 'types/auth';
import { UserDetail } from 'types/user';
import ToastHelper from 'utils/toast';
import SignupView from './Signup.view';

const SignupContainer = (props: SignupProps) => {
  const navigate = useNavigate();
  const toast = ToastHelper;

  const goToLogin = () => {
    navigate(routesSettings.AUTH_LOGIN.path, {
      replace: true,
    });
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: RegisterPayload) => {
      payload.role = 'customer';
      payload.username = payload.email;
      return register(payload);
    },
    onSuccess: (data) => {
      const id = data.id ?? data._id;
      const tokens = data.tokens;
      const username = data.username;
      $session.actions.setSession({
        id,
        tokens,
        username,
        isAuth: false,
      });
      $user.actions.setUser(data as unknown as UserDetail);
      navigate(routesSettings.AUTH_ACCOUNT_CREATED.path, {
        replace: true,
      });
    },
  });

  const { mutate: onGoogleSignup, isPending: googleSignupPending } =
    useMutation({
      mutationFn: (payload: GoogleSignupPayload) => googleLogin(payload),
      onSuccess: (data) => {
        const isExistingUser = data.isExistingUser;
        const id = data.id ?? data._id;
        const tokens = data.tokens;
        const username = data.username;
        $session.actions.setSession({
          id,
          tokens,
          username,
          isAuth: isExistingUser,
        });
        $user.actions.setUser(data as unknown as UserDetail);
        if (isExistingUser) {
          navigate(routesSettings.HOME.path, {
            replace: true,
          });
        } else {
          navigate(routesSettings.AUTH_ACCOUNT_CREATED.path, {
            replace: true,
          });
        }
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || error?.message;

        if (error?.response?.status === 400) {
          toast.error(
            errorMessage || 'Google signup failed. Please try again.',
          );
        } else if (error?.response?.status === 401) {
          toast.error('Google authentication failed. Invalid credentials.');
        } else if (error?.response?.status === 500) {
          toast.error('Server error. Please try again later.');
        } else {
          toast.error(
            errorMessage ||
              'Google signup failed. Please check your Google Client ID configuration.',
          );
        }

        console.error('Google signup error:', error);
      },
    });

  return (
    <SignupView
      isSubmitting={isPending}
      isGoogleLoading={googleSignupPending}
      onSubmit={mutate}
      onGoogleSignup={onGoogleSignup}
      goToLogin={goToLogin}
    />
  );
};

export default SignupContainer;
